import { useState, useCallback, useRef } from 'react';
import { parseSketchGeometry } from '../utils/sketchGeometry';
export function useServerArtifactEdit(params) {
    const { tools, setTool, dismissServerSelection, hideServerArtifact, showServerArtifact } = params;
    const [editingState, setEditingState] = useState(null);
    const editingRef = useRef(null);
    const [viewingState, setViewingState] = useState(null);
    const viewingRef = useRef(null);
    const startEdit = useCallback((artifact) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
        try {
            const geometry = JSON.parse(artifact.geometryJson);
            let unsavedId = null;
            let tool = null;
            switch (artifact.artifactType) {
                case 'placemark': {
                    const savedScale = typeof geometry.scale === 'number' ? geometry.scale : undefined;
                    unsavedId = tools.placemarksTool.addPlacemark(geometry.lat, geometry.lng, artifact.name, artifact.color, artifact.icon, artifact.showLabel, savedScale);
                    tool = 'placemark';
                    break;
                }
                case 'measurement': {
                    unsavedId = tools.measureTool.addMeasurementFromPoints(geometry.startLat, geometry.startLng, geometry.endLat, geometry.endLng, artifact.name, artifact.color, geometry.lineWidth, geometry.showLabel);
                    tool = 'measure';
                    break;
                }
                case 'measurement_circle': {
                    unsavedId = (_c = (_b = (_a = tools.measureTool).addCircleFromPoints) === null || _b === void 0 ? void 0 : _b.call(_a, geometry.centerLat, geometry.centerLng, geometry.radiusMeters, artifact.name, artifact.color, geometry.lineWidth, geometry.areaUnit, geometry.outlineColor, geometry.fillOpacity, geometry.showLabel)) !== null && _c !== void 0 ? _c : null;
                    tool = 'measure';
                    break;
                }
                case 'path': {
                    unsavedId = tools.pathsTool.createPathFromCoordinates(geometry.points, artifact.name, artifact.color, geometry.lineWidth);
                    if (unsavedId && ((_e = (_d = geometry.buffer) === null || _d === void 0 ? void 0 : _d.polygons) === null || _e === void 0 ? void 0 : _e.length) > 0 && tools.pathsTool.restoreBuffer) {
                        tools.pathsTool.restoreBuffer(unsavedId, {
                            distanceMeters: geometry.buffer.distance_meters,
                            unit: geometry.buffer.unit === 'm' ? 'm' : 'ft',
                            polygons: geometry.buffer.polygons,
                            areaSqMeters: typeof geometry.buffer.area_sq_meters === 'number'
                                ? geometry.buffer.area_sq_meters
                                : undefined,
                        });
                    }
                    tool = 'path';
                    break;
                }
                case 'polygon': {
                    unsavedId = tools.polygonTool.createPolygonFromCoordinates(geometry.vertices, artifact.name, artifact.color, geometry.lineWidth, geometry.fillOpacity, geometry.outlineColor);
                    tool = 'polygon';
                    break;
                }
                case 'stream_highlight': {
                    unsavedId = tools.streamHighlight.startEditFromArtifact(geometry, artifact.name, artifact.color, artifact.id);
                    // Stream/contour highlights no longer have a toolbar tool —
                    // the dialog opens via the hook's pendingClick + editBaselineRef.
                    // Sentinel keeps the editing-state branch below active.
                    tool = null;
                    break;
                }
                case 'contour_highlight': {
                    unsavedId = tools.contourHighlight.startEditFromArtifact(geometry, artifact.name, artifact.color, artifact.id);
                    tool = null;
                    break;
                }
                case 'sketch': {
                    // Re-parsed through the shared reader rather than taken off the raw
                    // JSON above, so the canvas gets the same clamped stroke records the
                    // renderer draws.
                    unsavedId = tools.sketchTool.loadStrokes(parseSketchGeometry(artifact.geometryJson).strokes);
                    tool = 'sketch';
                    break;
                }
            }
            // Gate on unsavedId so stream/contour highlights (which don't arm
            // a markup tool any more — tool === null) still flow through.
            if (unsavedId) {
                const state = {
                    serverArtifactId: artifact.id,
                    folderId: artifact.folderId,
                    artifactType: artifact.artifactType,
                    unsavedItemId: unsavedId,
                };
                setEditingState(state);
                editingRef.current = state;
                hideServerArtifact(artifact.id);
                dismissServerSelection();
                setTool(tool);
                // Open the unsaved item's options dialog so all metadata controls are
                // reachable during edit (instead of forcing the user to click the
                // loaded feature on the map again).
                switch (artifact.artifactType) {
                    case 'placemark':
                        (_g = (_f = tools.placemarksTool).selectPlacemarkById) === null || _g === void 0 ? void 0 : _g.call(_f, unsavedId);
                        break;
                    case 'measurement':
                        (_j = (_h = tools.measureTool).selectMeasurementById) === null || _j === void 0 ? void 0 : _j.call(_h, unsavedId);
                        break;
                    case 'measurement_circle':
                        (_l = (_k = tools.measureTool).selectCircleById) === null || _l === void 0 ? void 0 : _l.call(_k, unsavedId);
                        break;
                    case 'path':
                        (_o = (_m = tools.pathsTool).selectPathById) === null || _o === void 0 ? void 0 : _o.call(_m, unsavedId);
                        break;
                    case 'polygon':
                        (_q = (_p = tools.polygonTool).selectPolygonById) === null || _q === void 0 ? void 0 : _q.call(_p, unsavedId);
                        break;
                    // stream_highlight / contour_highlight: the hook already
                    // opened the dialog via pendingClick — no selection callback
                    // needed.
                }
            }
        }
        catch (err) {
            console.error('[ServerArtifactEdit] Failed to start edit:', err);
        }
    }, [tools, setTool, dismissServerSelection, hideServerArtifact]);
    const cancelEdit = useCallback(() => {
        const state = editingRef.current;
        if (state) {
            showServerArtifact(state.serverArtifactId);
        }
        setEditingState(null);
        editingRef.current = null;
    }, [showServerArtifact]);
    // View mode: load a saved path locally just to show it in the dialog (read-only).
    // Same loader as startEdit, but doesn't activate the markup tool and stores the
    // result in viewingState — keeps the "Save Changes / Cancel Edit" bottom panel
    // hidden until the user explicitly clicks Edit.
    const startView = useCallback((artifact) => {
        var _a, _b, _c, _d, _e, _f;
        if (artifact.artifactType !== 'path' && artifact.artifactType !== 'polygon' && artifact.artifactType !== 'stream_highlight')
            return;
        try {
            const geometry = JSON.parse(artifact.geometryJson);
            let unsavedId = null;
            if (artifact.artifactType === 'path') {
                unsavedId = tools.pathsTool.createPathFromCoordinates(geometry.points, artifact.name, artifact.color, geometry.lineWidth);
                if (unsavedId && ((_b = (_a = geometry.buffer) === null || _a === void 0 ? void 0 : _a.polygons) === null || _b === void 0 ? void 0 : _b.length) > 0 && tools.pathsTool.restoreBuffer) {
                    tools.pathsTool.restoreBuffer(unsavedId, {
                        distanceMeters: geometry.buffer.distance_meters,
                        unit: geometry.buffer.unit === 'm' ? 'm' : 'ft',
                        polygons: geometry.buffer.polygons,
                        areaSqMeters: typeof geometry.buffer.area_sq_meters === 'number'
                            ? geometry.buffer.area_sq_meters
                            : undefined,
                    });
                }
            }
            else if (artifact.artifactType === 'polygon') {
                unsavedId = tools.polygonTool.createPolygonFromCoordinates(geometry.vertices, artifact.name, artifact.color, geometry.lineWidth, geometry.fillOpacity, geometry.outlineColor);
            }
            else {
                // Stream: load via the same synth-entity path used for edit, but
                // gated on viewOnly so editBaselineRef stays null until the user
                // clicks Edit (which triggers promoteToEdit on the stream hook).
                unsavedId = tools.streamHighlight.startEditFromArtifact(geometry, artifact.name, artifact.color, artifact.id, true);
            }
            if (!unsavedId)
                return;
            const state = {
                serverArtifactId: artifact.id,
                folderId: artifact.folderId,
                artifactType: artifact.artifactType,
                unsavedItemId: unsavedId,
            };
            setViewingState(state);
            viewingRef.current = state;
            hideServerArtifact(artifact.id);
            dismissServerSelection();
            // Keep markup tool inactive — view mode doesn't allow edits via the map.
            setTool(null);
            if (artifact.artifactType === 'path') {
                (_d = (_c = tools.pathsTool).selectPathById) === null || _d === void 0 ? void 0 : _d.call(_c, unsavedId);
            }
            else if (artifact.artifactType === 'polygon') {
                (_f = (_e = tools.polygonTool).selectPolygonById) === null || _f === void 0 ? void 0 : _f.call(_e, unsavedId);
            }
            // Stream: dialog already opens via the hook's pendingClick.
        }
        catch (err) {
            console.error('[ServerArtifactEdit] Failed to start view:', err);
        }
    }, [tools, setTool, dismissServerSelection, hideServerArtifact]);
    const cancelView = useCallback(() => {
        const state = viewingRef.current;
        if (state) {
            showServerArtifact(state.serverArtifactId);
        }
        setViewingState(null);
        viewingRef.current = null;
    }, [showServerArtifact]);
    // Click "Edit" in the dialog while in view mode: flip the loaded path from view
    // to edit. The path is already locally loaded; just move state from viewing → editing
    // and activate the path tool so map clicks work as edits.
    const promoteViewToEdit = useCallback(() => {
        const state = viewingRef.current;
        if (!state)
            return;
        setEditingState(state);
        editingRef.current = state;
        setViewingState(null);
        viewingRef.current = null;
        if (state.artifactType === 'path') {
            setTool('path');
        }
        else if (state.artifactType === 'polygon') {
            setTool('polygon');
        }
        else if (state.artifactType === 'stream_highlight') {
            // Streams don't have a markup tool to arm — flip the hook's baseline
            // so confirmHighlight takes the edit branch when the user saves.
            tools.streamHighlight.promoteToEdit();
        }
    }, [setTool, tools]);
    return {
        editingState,
        startEdit,
        cancelEdit,
        isEditing: editingState !== null,
        viewingState,
        startView,
        cancelView,
        promoteViewToEdit,
        isViewing: viewingState !== null,
    };
}
